import { SPMSGetShiftPlanResponse } from "../../../../types/shiftPlan";

/**
 * Baseline (RCC Proposed) plan identification — UTR-REFLEX-1163.
 *
 * The RCC Proposed Plan view surfaces the auto-generated "baseline" plan when it
 * exists, and a clean empty state otherwise. The baseline is the plan whose
 * `lastUpdatedBy` is the system user `ShiftPlanScheduler`; plans by any other
 * (operator) alias are Operator Override plans, not the baseline.
 */
const BASELINE_PLAN_CREATOR = 'shiftplanscheduler';

/** Normalize a creator field to a bare lowercase alias (drops any @domain). */
const normalizeCreator = (value?: string | boolean | number): string =>
  typeof value === 'string' ? value.trim().toLowerCase().split('@')[0] : '';

/** Whether a plan is the auto-generated baseline (RCC Proposed) plan. */
export const isBaselinePlan = (plan?: SPMSGetShiftPlanResponse): boolean => {
  if (!plan) {
    return false;
  }
  // getAllShiftPlans returns the owner alias as `lastUpdatedBy` (no createdBy on
  // list items). Confirmed against the real payload.
  return normalizeCreator(plan.lastUpdatedBy) === BASELINE_PLAN_CREATOR;
};

/**
 * The RCC Proposed (baseline) plan, if any: the latest ShiftPlanScheduler plan by
 * `updatedAt`. Intended rule is "latest before shift start", but the FE has no
 * shift-start timestamp — that boundary comes from the backend (UTR-REFLEX-1172).
 */
export const findBaselinePlan = (
  plans?: SPMSGetShiftPlanResponse[]
): SPMSGetShiftPlanResponse | undefined => {
  const baselinePlans = plans?.filter(isBaselinePlan);
  if (!baselinePlans || baselinePlans.length === 0) {
    return undefined;
  }
  return baselinePlans.reduce((latest, plan) => {
    const latestTime = new Date(latest.updatedAt as string).getTime();
    const planTime = new Date(plan.updatedAt as string).getTime();
    return planTime > latestTime ? plan : latest;
  });
};

/**
 * Plans shown under the Operator Override view: everything NOT generated by
 * ShiftPlanScheduler (i.e. operator-created plans). The RCC Proposed baseline is
 * excluded so the two views don't overlap.
 */
export const getOverridePlans = (
  plans?: SPMSGetShiftPlanResponse[]
): SPMSGetShiftPlanResponse[] => plans?.filter((plan) => !isBaselinePlan(plan)) ?? [];
